import React, { useEffect, useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";
import { DollarSign, CreditCard, Wallet, AlertTriangle, Users, FileText, ShoppingCart, Package, AlertCircle, CheckCircle2, ShieldAlert, MapPin, TrendingUp } from "lucide-react";

const fmt = (v) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);

// ¿La fecha cae dentro del periodo? (hoy / semana / mes / año / histórico)
const inPeriodo = (dateStr, periodo) => {
  if (periodo === "historico") return true;
  if (!dateStr) return false;
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d)) return false;
  const now = new Date();
  if (periodo === "dia") return d.toDateString() === now.toDateString();
  if (periodo === "mes") return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  if (periodo === "anio") return d.getFullYear() === now.getFullYear();
  if (periodo === "semana") {
    const start = new Date(now);
    const dow = (now.getDay() + 6) % 7; // lunes = 0
    start.setDate(now.getDate() - dow);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 7);
    return d >= start && d < end;
  }
  return true;
};

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [secAlertas, setSecAlertas] = useState(null);
  const [pedidosRaw, setPedidosRaw] = useState([]);
  const [ventasPeriodo, setVentasPeriodo] = useState("mes");
  const [ventasVend, setVentasVend] = useState("todos");
  const [d, setD] = useState({
    ventasDelMes: 0, cobradoDelMes: 0, saldoPorCobrar: 0, carteraVencida: 0,
    clientesActivos: 0, cotizacionesPendientes: 0, pedidosEnProceso: 0,
    remisionesPendientes: 0, topVendedores: [], alertas: [],
  });

  const load = async () => {
      try {
        const [pRes, cRes, rRes] = await Promise.all([
          api.get("/pedidos"), api.get("/cotizaciones"), api.get("/remisiones"),
        ]);
        const pData = pRes.data, cData = cRes.data, rData = rRes.data;
        setPedidosRaw(pData || []);
        const today = new Date();
        const currentMonthStr = today.toISOString().slice(0, 7);
        const todayStr = today.toISOString().split("T")[0];
        const ninetyAgo = new Date(today.getTime() - 90 * 864e5);
        const sevenAgo = new Date(today.getTime() - 7 * 864e5);

        let ventas = 0, cobrado = 0, saldo = 0, cartera = 0, pProceso = 0, cPend = 0, rPend = 0;
        const activeClients = new Set();
        const vendedoresSales = {};
        const alertas = [];

        (pData || []).forEach((p) => {
          const isFact = p.estatus_pago === "Pendiente de cobro" || p.estatus_pago === "Cobrado" || p.estatus === "Facturado" || !!p.factura || !!p.fecha_facturacion;
          const pDateStr = p.fecha_facturacion || p.fecha_pedido || (p.created_at || "").slice(0, 10);
          const ventaMonth = (p.fecha_pedido || (p.created_at || "").slice(0, 10) || "").slice(0, 7);
          if ((p.estatus || "") !== "Cancelado" && ventaMonth === currentMonthStr) {
            ventas += p.subtotal || 0;
            const sid = p.vendedor_asignado_id || "unassigned";
            const sname = p.vendedor?.full_name || "Sin Asignar";
            if (!vendedoresSales[sid]) vendedoresSales[sid] = { name: sname, sales: 0 };
            vendedoresSales[sid].sales += p.subtotal || 0;
          }
          if (p.estatus_pago === "Cobrado") {
            const payMonth = (p.fecha_pago || pDateStr || "").slice(0, 7);
            if (payMonth === currentMonthStr) cobrado += p.subtotal || 0;
          }
          if (p.estatus_pago === "Pendiente de cobro" || (isFact && p.estatus_pago !== "Cobrado")) {
            saldo += p.subtotal || 0;
            if (p.fecha_vencimiento && p.fecha_vencimiento < todayStr) {
              cartera += p.subtotal || 0;
              alertas.push(`Documento vencido: Pedido ${p.numero_pedido} - Cliente: ${p.cliente}`);
            }
          }
          if (p.estatus !== "Entregado" && p.estatus !== "Pedido cerrado" && p.estatus !== "Cancelado") {
            pProceso++;
            const pDate = new Date(p.fecha_pedido || p.created_at);
            if (pDate < sevenAgo) alertas.push(`Pedido detenido (>7 días): ${p.numero_pedido} - Cliente: ${p.cliente}`);
          }
          const pAct = new Date(p.fecha_pedido || p.created_at);
          if (pAct >= ninetyAgo && p.cliente_id) activeClients.add(p.cliente_id);
        });

        (cData || []).forEach((c) => {
          if (c.estatus === "Pendiente de autorización" || c.estatus === "Pendiente") cPend++;
          const cDate = new Date(c.fecha || c.created_at);
          if (cDate >= ninetyAgo && c.cliente_id) activeClients.add(c.cliente_id);
        });

        (rData || []).forEach((r) => {
          if (r.estatus !== "Entregado" && r.estatus !== "Cancelado") {
            rPend++;
            alertas.push(`Remisión pendiente: ${r.numero_remision || r.id} - Cliente: ${r.cliente}`);
          }
          const rDate = new Date(r.fecha || r.created_at);
          if (rDate >= ninetyAgo && r.cliente_id) activeClients.add(r.cliente_id);
        });

        const topV = Object.values(vendedoresSales).sort((a, b) => b.sales - a.sales).slice(0, 5);

        setD({
          ventasDelMes: ventas, cobradoDelMes: cobrado, saldoPorCobrar: saldo, carteraVencida: cartera,
          clientesActivos: activeClients.size, cotizacionesPendientes: cPend, pedidosEnProceso: pProceso,
          remisionesPendientes: rPend, topVendedores: topV, alertas,
        });
      } finally {
        setLoading(false);
      }
  };
  useEffect(() => { load(); }, []);
  useRefetchOnFocus(load);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/accesos/alertas").then(({ data }) => setSecAlertas(data)).catch(() => {});
  }, [isAdmin]);

  const kpis = [
    { label: "Ventas del Mes", value: fmt(d.ventasDelMes), icon: DollarSign, color: "text-emerald-600", tid: "kpi-ventas-mes" },
    { label: "Cobrado del Mes", value: fmt(d.cobradoDelMes), icon: CreditCard, color: "text-blue-600", tid: "kpi-cobrado-mes" },
    { label: "Saldo por Cobrar", value: fmt(d.saldoPorCobrar), icon: Wallet, color: "text-amber-600", tid: "kpi-saldo" },
    { label: "Cartera Vencida", value: fmt(d.carteraVencida), icon: AlertTriangle, color: d.carteraVencida > 0 ? "text-red-600" : "text-slate-900", tid: "kpi-cartera-vencida" },
  ];
  const ventasVendedores = useMemo(() => {
    const set = new Set();
    pedidosRaw.forEach((p) => { const n = p.vendedor?.full_name; if (n) set.add(n); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [pedidosRaw]);

  const ventas = useMemo(() => {
    const isPaid = (p) => p.estatus === "Pago" || p.estatus === "Pedido cerrado" || p.estatus_pago === "Cobrado" || !!p.fecha_pago;
    const base = pedidosRaw.filter((p) => {
      if ((p.estatus || "") === "Cancelado") return false;
      if (isAdmin && ventasVend !== "todos" && (p.vendedor?.full_name || "") !== ventasVend) return false;
      const f = p.fecha_pedido || (p.created_at || "").slice(0, 10);
      return inPeriodo(f, ventasPeriodo);
    });
    let total = 0, cobrado = 0, porCobrar = 0;
    const porVend = {};
    base.forEach((p) => {
      const monto = Number(p.subtotal || 0);
      const pagado = isPaid(p);
      total += monto;
      if (pagado) cobrado += monto; else porCobrar += monto;
      const v = p.vendedor?.full_name || "Sin Asignar";
      if (!porVend[v]) porVend[v] = { name: v, sales: 0, cobrado: 0, porCobrar: 0, count: 0 };
      porVend[v].sales += monto;
      if (pagado) porVend[v].cobrado += monto; else porVend[v].porCobrar += monto;
      porVend[v].count++;
    });
    return { total, cobrado, porCobrar, count: base.length, lista: Object.values(porVend).sort((a, b) => b.sales - a.sales) };
  }, [pedidosRaw, ventasPeriodo, ventasVend, isAdmin]);

  const ops = [
    { label: "Clientes Activos", value: d.clientesActivos, icon: Users, tid: "kpi-clientes-activos" },
    { label: "Cotizaciones Pendientes", value: d.cotizacionesPendientes, icon: FileText, tid: "kpi-cotizaciones-pendientes" },
    { label: "Pedidos en Proceso", value: d.pedidosEnProceso, icon: ShoppingCart, tid: "kpi-pedidos-proceso" },
    { label: "Remisiones Pendientes", value: d.remisionesPendientes, icon: Package, tid: "kpi-remisiones-pendientes" },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-8" data-testid="dashboard">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Panel Ejecutivo</h1>
        <p className="text-slate-500 mt-2">Ventas, cartera, pedidos y desempeño comercial.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <div className="space-y-8">
          <div>
            <h2 className="text-lg font-semibold mb-4 text-slate-700">Indicadores Financieros</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {kpis.map((k) => (
                <Card key={k.label} data-testid={k.tid}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600">{k.label}</CardTitle>
                    <k.icon className={`h-4 w-4 ${k.color}`} />
                  </CardHeader>
                  <CardContent>
                    <div className={`text-2xl font-bold ${k.color}`}>{k.value}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-4 text-slate-700">Indicadores Operativos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {ops.map((k) => (
                <Card key={k.label} data-testid={k.tid}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600">{k.label}</CardTitle>
                    <k.icon className="h-4 w-4 text-slate-400" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-slate-900">{k.value}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div data-testid="ventas-totalizador">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold text-slate-700">Ventas por periodo</h2>
              <div className="flex flex-wrap items-center gap-2">
                {isAdmin && (
                  <Select value={ventasVend} onValueChange={setVentasVend}>
                    <SelectTrigger className="h-9 w-52" data-testid="ventas-vendedor"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos los vendedores</SelectItem>
                      {ventasVendedores.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Select value={ventasPeriodo} onValueChange={setVentasPeriodo}>
                  <SelectTrigger className="h-9 w-40" data-testid="ventas-periodo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dia">Hoy</SelectItem>
                    <SelectItem value="semana">Esta semana</SelectItem>
                    <SelectItem value="mes">Este mes</SelectItem>
                    <SelectItem value="anio">Este año</SelectItem>
                    <SelectItem value="historico">Histórico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-l-4 border-l-slate-400">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-slate-500" /> Total de ventas{isAdmin && ventasVend !== "todos" ? ` · ${ventasVend}` : ""}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-slate-900" data-testid="ventas-total-monto">{fmt(ventas.total)}</div>
                  <p className="text-xs text-slate-500 mt-1">{ventas.count} pedido(s) · subtotal sin IVA</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-emerald-500">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-emerald-700">Cobradas (cliente ya pagó)</CardTitle></CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-emerald-600" data-testid="ventas-cobradas-monto">{fmt(ventas.cobrado)}</div>
                  <p className="text-xs text-slate-500 mt-1">Ya ingresó a caja</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-amber-500">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-amber-700">Por cobrar (cliente no ha pagado)</CardTitle></CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-amber-600" data-testid="ventas-porcobrar-monto">{fmt(ventas.porCobrar)}</div>
                  <p className="text-xs text-slate-500 mt-1">Pendiente de pago del cliente</p>
                </CardContent>
              </Card>
            </div>
            <Card className="mt-4">
              <CardHeader className="pb-2"><CardTitle className="text-base">Ventas por vendedor</CardTitle></CardHeader>
              <CardContent>
                {ventas.lista.length === 0 ? (
                  <div className="text-center py-6 text-slate-400 text-sm">Sin ventas en este periodo.</div>
                ) : (
                  <div className="rounded-md border max-h-[280px] overflow-y-auto">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Vendedor</TableHead>
                        <TableHead className="text-right">Pedidos</TableHead>
                        <TableHead className="text-right text-emerald-700">Cobrado</TableHead>
                        <TableHead className="text-right text-amber-700">Por cobrar</TableHead>
                        <TableHead className="text-right">Ventas</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {ventas.lista.map((v, i) => (
                          <TableRow key={i} data-testid={`ventas-vend-row-${i}`}>
                            <TableCell className="font-medium">{v.name}</TableCell>
                            <TableCell className="text-right">{v.count}</TableCell>
                            <TableCell className="text-right text-emerald-600">{fmt(v.cobrado)}</TableCell>
                            <TableCell className="text-right text-amber-600">{fmt(v.porCobrar)}</TableCell>
                            <TableCell className="text-right font-semibold">{fmt(v.sales)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {isAdmin && secAlertas && (secAlertas.total_fallidos_24h > 0 || (secAlertas.ubicaciones_inusuales || []).length > 0) && (
            <Card className="border-l-4 border-l-red-500" data-testid="dash-seguridad-alertas">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-red-500" /> Alertas de seguridad
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {secAlertas.total_fallidos_24h > 0 && (
                    <div className="bg-red-50 text-red-900 p-3 rounded-md text-sm border border-red-100" data-testid="alerta-fallidos">
                      <span className="font-semibold">{secAlertas.total_fallidos_24h}</span> intento(s) de acceso fallido(s) en las últimas 24 h
                      {(secAlertas.por_usuario || []).length > 0 && (
                        <span> — {secAlertas.por_usuario.map((u) => `${u.username} (${u.intentos})`).join(", ")}</span>
                      )}
                    </div>
                  )}
                  {(secAlertas.ubicaciones_inusuales || []).map((u, i) => (
                    <div key={i} className="bg-red-50 text-red-900 p-3 rounded-md text-sm border border-red-100 flex items-start gap-2" data-testid="alerta-ubicacion">
                      <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>Acceso desde ubicación inusual: <b>{u.username}</b> — {[u.city, u.country].filter(Boolean).join(", ")}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card>
              <CardHeader><CardTitle className="text-lg">Top Vendedores del Mes</CardTitle></CardHeader>
              <CardContent>
                {d.topVendedores.length === 0 ? (
                  <div className="text-center py-6 text-slate-400">No hay ventas registradas este mes.</div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow><TableHead>Vendedor</TableHead><TableHead className="text-right">Ventas</TableHead></TableRow>
                      </TableHeader>
                      <TableBody>
                        {d.topVendedores.map((v, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{v.name}</TableCell>
                            <TableCell className="text-right">{fmt(v.sales)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-amber-500">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-500" /> Alertas Operativas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {d.alertas.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-emerald-600 gap-3">
                    <CheckCircle2 className="h-10 w-10" />
                    <span className="font-medium">Sin alertas operativas</span>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[250px] overflow-y-auto pr-2">
                    {d.alertas.map((a, i) => (
                      <div key={i} className="bg-amber-50 text-amber-900 p-3 rounded-md text-sm border border-amber-100">
                        {a}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
